// Контракт API пульта: экран «Почта» (план MAIL, A9) — что Анна сделала с каждым письмом отдела квалификации.
//
// - `GET /api/v1/mail/triage?period&mailbox&category&limit` (любой вошедший) → `{ items, total, counts, mailboxes, categories,
//   labelsAvailable, at }`: строки сортировки писем (`mail_triage`), новые сверху. Период — как у «Диалогов» (`today`, `7d`,
//   `30d`, `all`; по умолчанию 7 дней). `category` — вид письма (код из `categories`) или `staff` — переписка сотрудников. Адрес
//   отправителя, тему и цитату роли без права видеть персональные данные сервер отдаёт маской (`maskEmail`, `maskContactsInText`:
//   адреса и телефоны в тексте);
//   адрес и тему письма сервер читает у Омнидеска — не ответил, строки приходят без них (`labelsAvailable: false`);
// - `PUT /api/v1/mail/triage/:id { category }` (admin) → `{ item }`: человек поправил вид письма («Разобрала неверно…»). Запись в
//   журнал `audit_log` (`mail.triage.fix`); вид пишется в `mail_triage.fixed_category`, отсюда флаг `triage_fixed` в ЕЦПР. Тот же
//   вид, что назвала Анна, — поправка снимается.
//
// Перечисления не копируются: пульт получает виды письма с подписями в ответе (`categories`), а слова состояния и маршрута
// — в каждой строке (`statusRu`, `routeRu`, `reasonRu`).
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { MomentSchema, StatusToneSchema } from './common.js';
import { PERIODS } from './dialogs.js';

/** Сколько строк на экране: умолчание и предел. */
export const MAIL_TRIAGE_LIMIT_DEFAULT = 100;
export const MAIL_TRIAGE_LIMIT_MAX = 200;

const MAILBOX = /^[a-z0-9._-]{1,64}$/;
const CATEGORY = /^[a-z_]{1,20}$/;

/** Параметры списка из строки запроса. */
export const MailTriageQuerySchema = z.object({
  period: z.enum(PERIODS, { error: `period — одно из: ${PERIODS.join(', ')}.` }).default('7d'),
  mailbox: z.string({ error: 'mailbox — ящик: часть адреса до @.' }).regex(MAILBOX, 'mailbox — ящик вроде kval или info.').optional(),
  category: z.string({ error: 'category — вид письма.' }).regex(CATEGORY, 'category — код вида письма вроде lead или staff.').optional(),
  limit: z.coerce
    .number({ error: `limit — сколько писем на экране: целое число от 1 до ${String(MAIL_TRIAGE_LIMIT_MAX)}.` })
    .int(`limit — целое число от 1 до ${String(MAIL_TRIAGE_LIMIT_MAX)}.`)
    .min(1, `limit — целое число от 1 до ${String(MAIL_TRIAGE_LIMIT_MAX)}.`)
    .max(MAIL_TRIAGE_LIMIT_MAX, `limit — целое число от 1 до ${String(MAIL_TRIAGE_LIMIT_MAX)}.`)
    .default(MAIL_TRIAGE_LIMIT_DEFAULT),
});
export type MailTriageQuery = z.infer<typeof MailTriageQuerySchema>;

/**
 * Строка экрана: письмо и что с ним сделала Анна. `kind` — вид письма, как его видят ЕЦПР и пульт (вид Анны; ворота кода без
 * вида — «служебное», модель не ответила — «не уверена»); `null` — переписка сотрудников (в счётчики не входит). `fixedKind` — вид,
 * который назначил человек (только если он отличается от вида Анны).
 */
export const MailTriageRowSchema = z.object({
  id: z.uuid(),
  at: MomentSchema,
  ticketId: z.string().min(1),
  ticketNumber: z.number().int().positive().nullable(),
  dialogId: z.uuid(),
  /** Ящик — часть адреса до «@» (`kval`); неизвестен — `null`. */
  mailbox: z.string().nullable(),
  /** Адрес отправителя; у роли без права видеть персональные данные — маска «i•••@mail.ru». `null` — Омнидеск адрес не дал. */
  sender: z.string().nullable(),
  subject: z.string().nullable(),
  kind: z.string().nullable(),
  kindRu: z.string().min(1),
  /** Уверенность модели: `high`, `medium`, `low`; модель не вызывалась — `null`. */
  confidence: z.enum(['high', 'medium', 'low']).nullable(),
  /** Цитата письма, на которую опирается вид (проверена кодом). */
  quote: z.string().nullable(),
  fixedKind: z.string().nullable(),
  fixedKindRu: z.string().nullable(),
  /** Почему письмо не пустили к Анне ворота кода («Автоответ», «Рассылка»); пропустили — `null`. */
  gateRu: z.string().nullable(),
  route: z.string().min(1),
  routeRu: z.string().min(1),
  /** Причина маршрута словами. */
  reasonRu: z.string().min(1),
  status: z.string().min(1),
  statusRu: z.string().min(1),
  statusTone: StatusToneSchema,
  /** Что ответил Омнидеск или почему действие не вышло. */
  detail: z.string().nullable(),
  mode: z.enum(['shadow', 'assist', 'live']),
  existingClient: z.boolean(),
  language: z.enum(['ru', 'other']),
  attachmentsOnly: z.boolean(),
  costUsd: z.number().nonnegative(),
});
export type MailTriageRow = z.infer<typeof MailTriageRowSchema>;

/** Вид письма словами: код — для запроса и поправки, подпись — для людей. */
export const MailCategoryOptionSchema = z.object({ code: z.string().min(1), labelRu: z.string().min(1) });
export type MailCategoryOption = z.infer<typeof MailCategoryOptionSchema>;

export const MailTriageListResponseSchema = z.object({
  items: z.array(MailTriageRowSchema),
  /** Писем за период при выбранном ящике и виде (на экране — не больше `limit`). */
  total: z.number().int().nonnegative(),
  /** Писем по видам за период при выбранном ящике (без фильтра по виду): код вида → число; `staff` — переписка сотрудников. */
  counts: z.record(z.string(), z.number().int().nonnegative()),
  /** Ящики, которые встречаются в сортировке за всё время. */
  mailboxes: z.array(z.string()),
  /** Виды письма с подписями — для фильтра и для «Разобрала неверно…». */
  categories: z.array(MailCategoryOptionSchema),
  /** Тему и адрес отправителя прочитали у Омнидеска; `false` — Омнидеск не ответил, строки без них. */
  labelsAvailable: z.boolean(),
  at: MomentSchema,
});
export type MailTriageListResponse = z.infer<typeof MailTriageListResponseSchema>;

/** Тело `PUT /mail/triage/:id`: вид письма, который назначил человек. */
export const PutMailTriageBodySchema = z.object({ category: z.string().regex(CATEGORY) });
export type PutMailTriageBody = z.infer<typeof PutMailTriageBodySchema>;

export const PutMailTriageResponseSchema = z.object({ item: MailTriageRowSchema });
export type PutMailTriageResponse = z.infer<typeof PutMailTriageResponseSchema>;

/** Тексты отказов поправки вида письма. */
export const MAIL_TRIAGE_ERRORS_RU = {
  notFound: 'Письмо не найдено: его строки сортировки нет.',
  badCategory: 'category — один из видов письма: запрос клиента, действующий клиент, поставщик, реклама, резюме, документы, служебное, не уверена.',
  bodyShape: 'Тело запроса — JSON вида {"category": "supplier"}.',
} as const;
